import React, { useState, useEffect, useMemo, useContext, useRef } from 'react';
import { Building } from 'react-bootstrap-icons';
import { Select } from 'antd';

import { AuthContext } from '../../../services/auth.service';

import './styles.scss';

const TIER_MAP = { essentials: 4, advanced: 3, premium: 2, professional: 2, enterprise: 1 };

const getHighestTier = (company) => {
    const tiers = [
        company?.tag_monitor_subscription_model,
        company?.datalayer_guard_subscription_model,
        company?.sitespeed_monitor_subscription_model,
    ].map((plan) => TIER_MAP[plan?.toLowerCase()] ?? 0).filter(Boolean);
    return tiers.length ? Math.min(...tiers) : null;
};

const Dropdown = (props) => {
    const { list, hovered, hoverOutSidebar } = props;
    const { companyId, handleCompanyChange, adminUser } = useContext(AuthContext);

    const [selectedCompanyId, setSelectedCompanyId] = useState(companyId ?? undefined);
    const [searchValue, setSearchValue] = useState('');
    const selectRef = useRef(null);

    const sortedList = useMemo(() => (Array.isArray(list) ? [...list].sort((a, b) => {
        const nameA = a?.name.toLowerCase();
        const nameB = b?.name.toLowerCase();

        if (nameA < nameB) return -1;
        else if (nameA > nameB) return 1;

        return 0;
    }) : []), [list]);

    const groupedList = useMemo(() => {
        if (!adminUser) return null;

        const activeCompanies = sortedList.filter((company) => company?.active !== false);
        const inactiveCompanies = sortedList.filter((company) => company?.active === false);

        return {
            active: activeCompanies,
            inactive: inactiveCompanies,
        };
    }, [adminUser, sortedList]);

    const flatOptions = useMemo(() => {
        const search = searchValue.toLowerCase();
        return sortedList
            .filter((company) => !search || company?.name?.toLowerCase().includes(search))
            .map((company) => ({ value: company?.id, label: company?.name }));
    }, [sortedList, searchValue]);

    const groupedOptions = useMemo(() => {
        if (!adminUser || !groupedList) return [];

        const search = searchValue.toLowerCase();
        const filterBySearch = (company) => !search || company?.name?.toLowerCase().includes(search);

        const filteredActive = groupedList.active.filter(filterBySearch);
        const filteredInactive = groupedList.inactive.filter(filterBySearch);

        const options = [];

        const buildOption = (company) => {
            const tier = getHighestTier(company);
            return {
                value: company?.id,
                label: (
                    <span className="company-option">
                        <span className="company-option__name">{company?.name}</span>
                        {tier && <span className="company-option__tier">{tier}</span>}
                    </span>
                ),
            };
        };

        if (filteredActive.length) {
            options.push({
                label: `Active (${filteredActive.length})`,
                options: filteredActive.map(buildOption),
            });
        }

        if (filteredInactive.length) {
            options.push({
                label: `Inactive (${filteredInactive.length})`,
                options: filteredInactive.map(buildOption),
            });
        }

        return options;
    }, [adminUser, groupedList, searchValue]);

    useEffect(() => {
        setSelectedCompanyId(companyId ?? undefined);
    }, [companyId]);

    useEffect(() => {
        if (!hovered) {
            selectRef.current?.blur();
            setSearchValue('');
        }
    }, [hovered]);

    const handleDropdownValueChange = (value) => {
        const chosenCompanyId = Number(value);
        const company = list?.find((elem) => elem.id === chosenCompanyId);

        if (!company) return;

        setSelectedCompanyId(company?.id);
        handleCompanyChange(company?.id);

        hoverOutSidebar();
    };

    return (
        <div className={`company-list-block ${hovered ? "is-expanded" : "is-collapsed"}`}>
            <Building color="black" size={16} />
            <Select
                ref={selectRef}
                className={`company-list ${hovered ? "expanded" : ""}`}
                popupClassName="company-list-dropdown"
                value={selectedCompanyId}
                onChange={handleDropdownValueChange}
                options={adminUser ? groupedOptions : flatOptions}
                showSearch
                filterOption={false}
                onSearch={setSearchValue}
                onDropdownVisibleChange={(open) => { if (!open) setSearchValue(''); }}
                suffixIcon={null}
                getPopupContainer={(triggerNode) => triggerNode.parentElement}
                aria-label="Company selector"
            />
        </div>
    );
};

export default Dropdown;
